import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import TwDialog from '../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import Grid from '@mui/material/Grid';
import QuickMenuItems from '../CreateOrder/QuickMenuItems/QuickMenuItems';
import TripWrapperForm from '../CreateTrip/TripWrapperForm/TripWrapperForm';
import TripOrderInfo from '../CreateTrip/TripOrderInfo/TripOrderInfo';
import TripRouteInfo from '../CreateTrip/TripRoute/TripRoute';
import DispatchInfo from '../CreateOrder/Dispatch/DispatchInfo';
import AttachmentsInfo from '../CreateOrder/Attachments/AttachmentsInfo';
import TwNotifyDriver, {
  INotifyDriverWithFullSegment
} from '../../components/TwNotifyDriver/TwNotifyDriver';
import useOnScreen from '../../hooks/useOnScreen';
import { INTERSECTION_OPTIONS } from '../CreateTrip/CreateTrip';
import useAuth from '../../providers/Auth/Auth.provider';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, Theme } from '@mui/material/styles';
import { EntityType } from '../../redux/Global.model';
import {
  clearTripStateExceptId,
  LtlSliceTrip,
  setTripSliceState
} from '../../redux/features/trip/tripSlice';
import { openEditTrip, setTripDetailReload } from '../../redux/features/navigation/navigationSlice';
import { useLazyGetTripByIdQuery, usePutTripMutation } from '../../redux/api/trips/tripApi';
import { SliceStop } from '../../redux/features/orders/ordersSlice';
import { nanoid } from '@reduxjs/toolkit';
import {
  STOP_TYPE,
  StopStatus,
  Trip,
  TripCommodities,
  TripDriverInfo,
  TripStop,
  UpsertTrip
} from '../../redux/api/trips/trips.model';
import DateUtils from '../../utils/dateUtils';
import { TwAutocompleteOption } from '../../components/TwAutocomplete/TwAutocomplete';
import StringUtils from '../../utils/stringUtils';
import NumberUtils from '../../utils/numberUtils';
import DriverUtils from '../../utils/driver/driverUtils';
import { logRocketTrack } from '../../utils/logRocketUtils';
import {
  useUpdateManyDocumentsMutation,
  useUploadOrderTripDocumentMutation
} from '../../redux/api/orders/ordersApi';
import TwWarningHelper from '../../components/TwWarningHelper/TwWarningHelper';
import {
  FLOWS,
  Validation,
  ValidationsResponse
} from '../../redux/api/validationsHelper/validationsHelper.model';
import { useValidateDispatchMutation } from '../../redux/api/validationsHelper/validationsApi';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import _ from 'lodash';
import { PROVIDER } from '../../providers/User/User.model';
import { TwLeaveWarning } from '../../components/TwLeaveWarning/TwLeaveWarning';
import TwDriverPayConfirmationModal from '../../components/TwDriverPayCalc/TwDriverPayConfirmationModal';
import {
  mapCommodityListToTripCommoditiesForSave,
  mapSavedTripCommodityToCommodityList
} from '../../utils/commodity/commodityUtils';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

interface EditTripProps {
  openEdit: boolean;
  onEditTrip?: () => void;
}

const DataContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  background: 'transparent',
  paddingBottom: '50vh'
}));

const EditTrip: FC<EditTripProps> = ({ openEdit, onEditTrip }) => {
  const {
    data: { userData }
  } = useAuth();
  const dispatch = useAppDispatch();
  const { addNotification } = useNotifications();
  const matchesAboveLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const filters = useAppSelector((state) => state.activeTrips.filters);
  const { tripId, driver_pay_confirmation_modal, ...tripData } = useAppSelector(
    (state) => state.trip
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const orderInfoRef = useRef<HTMLDivElement>(null);
  const routeInfoRef = useRef<HTMLDivElement>(null);
  const dispatchRef = useRef<HTMLDivElement>(null);
  const attachmentsInfoRef = useRef<HTMLDivElement>(null);

  const isOrderInfoVisible = useOnScreen(orderInfoRef, INTERSECTION_OPTIONS);
  const isRouteInfoVisible = useOnScreen(routeInfoRef, INTERSECTION_OPTIONS);
  const isDispatchInfoVisible = useOnScreen(dispatchRef, INTERSECTION_OPTIONS);
  const isAttachmentsVisible = useOnScreen(attachmentsInfoRef, INTERSECTION_OPTIONS);

  const [isOpenWarning, setIsOpenWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Notify driver component
  const [openNotify, setOpenNotify] = useState(false);
  const [driversToNotify, setDriversToNotify] = useState<INotifyDriverWithFullSegment[]>([]);
  const [unassignedDriversToNotify, setUnassignedDriversToNotify] = useState<
    INotifyDriverWithFullSegment[]
  >([]);
  const [originalTripData, setOriginalTripData] = useState<Trip | null>(null);

  // RTK Query
  const [getTripById] = useLazyGetTripByIdQuery();
  const [editTrip] = usePutTripMutation();
  const [uploadOrderTripDocument] = useUploadOrderTripDocumentMutation();
  const [updateManyDocuments] = useUpdateManyDocumentsMutation();

  // dispatch helper
  const { enableWarningHelper, enableSkipDispatchModal, enableCommoditiesDatabase } =
    useFeatureFlags();
  const [validations, setValidations] = useState<Validation[]>([]);
  const [openWarningHelper, setOpenWarningHelper] = useState(false);
  const [validateDispatch] = useValidateDispatchMutation();

  // #region functions

  const handleWarningClose = (): void => {
    setIsOpenWarning(false);
  };

  const handleCloseEditTrip = (): void => {
    onEditTrip && onEditTrip();
    setIsOpenWarning(false);
    dispatch(openEditTrip(false));
    dispatch(setTripDetailReload(true));

    dispatch(clearTripStateExceptId());
  };

  const closeNotify = (): void => {
    setOpenNotify(false);
    setDriversToNotify([]);
    handleCloseEditTrip();
  };

  const editTripAndUploadDocs = (): Promise<any> => {
    if (!tripId) return Promise.reject('Trip ID not found');

    return new Promise((resolve, reject) => {
      const tripRequest: UpsertTrip = {
        trip_commodities: [],
        instructions: tripData.instructions,
        dispatcher_id: tripData.selected_dispatcher || null,
        truck_id: tripData.selected_truck || null,
        trailer_id: tripData.selected_trailer || null,
        carrier_id: tripData.selected_carrier || null,
        owner_operator_id: tripData.selected_owner_operator || null,
        sort_order: tripData.sort_order || 1,
        is_empty_miles_auto: tripData.is_empty_miles_auto,
        is_loaded_miles_auto: tripData.is_loaded_miles_auto,
        empty_mileage_source: tripData.empty_miles ? tripData.empty_mileage_source || null : null,
        loaded_mileage_source: tripData.loaded_miles
          ? tripData.loaded_mileage_source || null
          : null,
        estimated_mileage: tripData.loaded_miles || null,
        empty_miles: tripData.empty_miles || null,
        stops: [],
        drivers: [],
        documents: tripData.existing_documents?.map((doc) => doc.id)
      };

      const cleanCommodities: TripCommodities[] = [];

      // clean commodities, remove empty ones
      if (enableCommoditiesDatabase) {
        tripData.new_trip_commodities?.forEach((commodity) => {
          if (
            (commodity.name === '' || commodity.name !== null) &&
            !commodity.quantity &&
            !commodity.weight
          ) {
            return;
          } else {
            cleanCommodities.push(mapCommodityListToTripCommoditiesForSave(commodity));
          }
        });
      } else {
        // clean commodities, remove empty ones
        tripData.trip_commodities.forEach((commodity: TripCommodities) => {
          if (commodity.description === '' && !commodity.quantity && !commodity.weight) {
            return;
          } else {
            const comm: TripCommodities = {
              description: commodity.description,
              quantity: commodity.quantity || 0,
              weight: commodity.weight || 0,
              ui_uid: commodity.ui_uid,
              order_commodity_id: commodity.id ? commodity.id : undefined,
              id: commodity.id ? commodity.id : undefined,
              packaging_unit: commodity.packaging_unit || null
            };

            cleanCommodities.push(comm);
          }
        });
      }

      if (cleanCommodities.length > 0) {
        tripRequest.trip_commodities = cleanCommodities;
      }

      // Drivers check
      const driversForTrip = tripData.selected_drivers.filter((d) => !!d.id);
      driversForTrip.length > 0
        ? (tripRequest.drivers = driversForTrip as TripDriverInfo[])
        : undefined;

      // Build stops
      tripRequest.stops = tripData.provider_trip_stops.map((s) => {
        const stop: TripStop = {
          id: s.id || undefined,
          order: s.order,
          sort_order: s.order,
          start_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
          start_time: s.provider_time_from
            ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_from))
            : undefined,
          end_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
          end_time: s.provider_time_to
            ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_to))
            : undefined,
          stop_type: (s.provider_stop_type?.id as STOP_TYPE) || undefined,
          notes: s.provider_include_location_notes ? s.notes : undefined,
          stop_identifier:
            s.provider_stop_type?.id && s.provider_stop_identifier
              ? s.provider_stop_identifier
              : undefined,
          contact: s.provider_include_location_notes ? s.contact : undefined,
          order_id: s.order_data?.order_id || undefined,
          is_out_time_overriden: !!s.provider_stop_out_time_overriden,
          is_in_time_overriden: !!s.provider_stop_in_time_overriden
        };

        if (s.provider_location?.id) {
          stop.location_id = s.provider_location.id;
        }

        if (s.provider_check_in_date && s.provider_check_in_time) {
          stop.in_time = DateUtils.getISODate(
            s.provider_check_in_date,
            s.provider_check_in_time ?? undefined
          );
        }

        if (s.provider_check_out_date && s.provider_check_out_time) {
          stop.out_time = DateUtils.getISODate(
            s.provider_check_out_date,
            s.provider_check_out_time ?? undefined
          );
        }

        return stop;
      });

      editTrip({
        id: tripId,
        body: tripRequest,
        searchTripParams: { filters },
        prevStatus: tripData.status
      })
        .unwrap()
        .then(async (newTrip) => {
          if (tripData.documents && tripData.documents?.length > 0) {
            const docsWithOrder = tripData.documents.map((d) => {
              return { ...d, tripId: tripId };
            });

            await Promise.all(
              docsWithOrder.map((doc) => uploadOrderTripDocument({ document: doc }).unwrap())
            ).catch((error) => {
              logRocketTrack('Upload', {
                action: 'Update document on edit trip',
                severity: 'high',
                tenant_id: userData?.tenant?.id,
                company_name: userData?.tenant?.company_name,
                email: userData?.email_address,
                user_first_name: userData?.first_name,
                user_last_name: userData?.last_name
              });
              addNotification({
                message: 'Trip edited, but documents failed to upload. Please re-upload.',
                variant: 'warning'
              });
            });
          }

          resolve(newTrip);
        })
        .then((newTrip) => {
          const documentToUpdate = tripData.existing_documents?.filter(
            (doc) => doc.updated_at && parseInt(doc.updated_at) <= parseInt(Date.now().toString())
          );

          if (documentToUpdate && documentToUpdate?.length > 0) {
            // shape to format needed to update
            const documentParams = documentToUpdate.map((doc) => {
              const obj = {
                id: doc.id,
                name: doc.attachment.name,
                type: doc.type,
                tripId: doc.trip_id,

                visibility: doc.visibility
              };

              return obj;
            });

            return updateManyDocuments(documentParams)
              .unwrap()
              .catch((error) => {
                console.error('Error updating documents', error);
                addNotification({
                  message: 'Trip edited, but documents to update failed. Please try again.',
                  variant: 'warning'
                });
              });
          }

          resolve(newTrip);
        })
        .catch((error) => {
          reject(error.data.message);
        });
    });
  };

  const handleFormSubmit = (): void => {
    if (formRef.current) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const handleSubmitEdit = async (ignoreAndProceed = false): Promise<void> => {
    setIsSubmitting(true);

    // dispatch helper validation

    let validationsResponse: ValidationsResponse | null = null;
    let hasValidations = false;
    if (enableWarningHelper && !ignoreAndProceed) {
      const stops = tripData.provider_trip_stops;
      const tripToValidate = stops[0];
      const lastTrip = stops[stops.length - 1];

      try {
        validationsResponse = await validateDispatch({
          data: {
            flow: FLOWS.TRIPS,
            order_id: tripId,
            trips: [
              {
                id: tripToValidate.id ? tripToValidate.id : undefined,
                drivers: tripData.selected_drivers
                  .filter((d) => d.id)
                  .map((d) => {
                    return { id: d.id } as TripDriverInfo;
                  }),
                truck_id: tripData.selected_truck || undefined,
                trailer_id: tripData.selected_trailer || undefined,
                start_date: tripToValidate.provider_date
                  ? DateUtils.getShortDate(tripToValidate.provider_date)
                  : undefined,
                stop_date: lastTrip.provider_date
                  ? DateUtils.getShortDate(lastTrip.provider_date)
                  : undefined,
                start_from_time: tripToValidate.provider_time_from
                  ? DateUtils.get24HourFormat(DateUtils.getTime(tripToValidate.provider_time_from))
                  : undefined,
                stop_from_time: lastTrip.provider_time_to
                  ? DateUtils.get24HourFormat(DateUtils.getTime(lastTrip.provider_time_to))
                  : lastTrip.provider_time_from
                  ? DateUtils.get24HourFormat(DateUtils.getTime(lastTrip.provider_time_from))
                  : undefined
              }
            ]
          }
        }).unwrap();
      } catch (error) {
        console.error(error);
      }
    }

    if (validationsResponse) {
      hasValidations = validationsResponse.data.length > 0;

      // check if there are new validations
      const hasNewValidations = !_.isEqual(validationsResponse.data, validations);

      if (!hasNewValidations) {
        handleIgnoreAndProceed();

        return;
      }

      if (!hasValidations) {
        handleIgnoreAndProceed();

        return;
      }

      setOpenWarningHelper(hasValidations);
      setValidations(validationsResponse.data);
    }

    if (!hasValidations || ignoreAndProceed || !enableWarningHelper) {
      editTripAndUploadDocs()
        .then((newTrip) => {
          if (!originalTripData) return;

          const driversDifference = DriverUtils.findDifferenceOfDriversToAssign(
            [originalTripData],
            [newTrip.data]
          );
          const driversUnassigned = DriverUtils.findUnassignedDrivers(
            [originalTripData],
            [newTrip.data]
          );

          if (driversDifference.length > 0) {
            driversDifference.map((segment) => {
              const selectedDriver = {
                driverId: segment.driverId,
                fullSegment: segment.fullSegment,
                driverIndex: segment.driverIndex,
                isForExistingLoadUpdate: segment.isForExistingLoadUpdate,
                deleted_trip_index: segment.deleted_trip_index
              };
              return selectedDriver;
            });

            setDriversToNotify(driversDifference);
          }

          if (driversUnassigned.length > 0) {
            setUnassignedDriversToNotify(driversUnassigned);
          }

          if (
            !enableSkipDispatchModal &&
            (driversDifference.length > 0 || driversUnassigned.length > 0)
          ) {
            setOpenNotify(true);
          } else {
            // close dialogs and open detail
            handleCloseEditTrip();
          }
        })
        .catch((err) => {
          console.error(err);
          addNotification({
            message: 'Error editing trip, please try again',
            variant: 'error'
          });
          logRocketTrack('Trip build', {
            action: err,
            severity: 'medium',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const tripResponseToSlice = (response: Trip): void => {
    const tripSlice: LtlSliceTrip = {
      tripId: tripId,
      readable_id: response.readable_id,
      order: 1,
      sort_order: 1,
      ui_id: tripId || nanoid(),
      selected_dispatcher: response.dispatcher_id || '',
      selected_carrier: response.carrier_id || '',
      selected_owner_operator: response.owner_operator_id || '',
      selected_trailer: response.trailer_id || '',
      selected_truck: response.truck_id || '',
      selected_drivers:
        response.drivers.length > 0
          ? (response.drivers.map((driver) => {
              const miles = response.estimated_mileage || 0;
              const loadedMiles =
                driver.driver_pay_override_data?.loaded_miles ||
                (response.drivers.length > 1 ? NumberUtils.roundNumber(miles / 2) : miles);

              const driverPayOverrideData = driver.driver_pay_override_data || null;

              const driverData: TripDriverInfo = {
                id: driver.id,
                send_driver_link: false,
                is_primary: driver.is_primary,
                trip_driver_pay_other_payment: driver.trip_driver_pay_other_payment || []
              };

              if (driverPayOverrideData) {
                driverData.driver_pay_override_data = {
                  ...driverPayOverrideData,
                  loaded_miles: loadedMiles || 0,
                  rate: driverPayOverrideData.rate || 0,
                  quantity: driverPayOverrideData.quantity || 0
                };
              }

              if (driver.driver_pay_override) {
                driverData.driver_pay_override = driver.driver_pay_override;
              }

              return {
                ...driverData
              };
            }) as Partial<TripDriverInfo>[])
          : [{ send_driver_link: false }],
      empty_miles: response.empty_miles,
      loaded_miles: response.estimated_mileage,
      is_empty_miles_auto: response.is_empty_miles_auto,
      is_loaded_miles_auto: response.is_loaded_miles_auto,
      empty_mileage_source: response.empty_mileage_source,
      loaded_mileage_source: response.loaded_mileage_source,
      instructions: response.instructions,
      trip_commodities: [],
      status: response.status,
      documents: [],
      existing_documents: response.trip_attachments || [],
      attachments: [],
      provider_trip_stops: [],
      ordersInTrip: response.orders
        ? response.orders?.map((order) => {
            return {
              id: order.id,
              readable_id: order.readable_id
            };
          })
        : [],
      total_trip_miles: null,
      driver_pay_confirmation_modal: {
        open: false,
        action: null
      }
    };

    // format trip stops to SliceStops
    const sliceStops: SliceStop[] = [];
    if (response.stops && response.stops.length > 0) {
      response.stops.forEach((stop, stopIndex) => {
        const order = response.orders?.find((order) => order.id === stop.order_id) || null;

        const providerStop: SliceStop = {
          id: stop.id,
          segment_id: tripId || '',
          ui_id: stop.id || nanoid(),
          order: stopIndex + 1,
          stop_identifier: stop.stop_identifier || '',
          provider_stop_identifier: stop.stop_identifier || '',
          provider_edi_stop_identifier: stop.edi_stop_identifier || undefined,
          provider_date:
            (stop.start_date && DateUtils.getDateInLocale(new Date(stop.start_date))) || null,
          provider_location: stop.location
            ? ({
                id: stop.location.id,
                label: stop.location.name,
                value: stop.location
              } as TwAutocompleteOption)
            : null,
          location: stop.location,
          provider_stop_type: stop.stop_type
            ? ({
                id: stop.stop_type,
                label: StringUtils.getLoadIntermediateStopLabel(stop.stop_type),
                value: stop.stop_type
              } as TwAutocompleteOption)
            : null,
          provider_time_from: stop.start_time
            ? DateUtils.convertTimeToDate(stop.start_time)
            : undefined,
          provider_time_to: stop.end_time ? DateUtils.convertTimeToDate(stop.end_time) : undefined,
          provider_include_location_notes: !!stop.notes || !!stop.contact,
          notes: stop.notes || '',
          contact: stop.contact,
          provider_stop_status: stop.status || StopStatus.notStarted,
          provider_check_in_date: stop.in_time ? new Date(stop.in_time) : null,
          provider_check_in_time: stop.in_time ? new Date(stop.in_time) : null,
          provider_stop_in_time_overriden: stop.is_in_time_overriden || undefined,
          provider_check_out_date: stop.out_time ? new Date(stop.out_time) : null,
          provider_check_out_time: stop.out_time ? new Date(stop.out_time) : null,
          provider_stop_out_time_overriden: stop.is_out_time_overriden || undefined,
          is_shared_stop: false
        };

        if (order && stop.order_id) {
          providerStop.order_data = {
            order_id: stop.order_id,
            order_readable_id: order.readable_id
          };
        }

        sliceStops.push(providerStop);
      });
    }

    tripSlice.provider_trip_stops = sliceStops;

    // format trip commodities

    if (response.trip_commodities && response.trip_commodities.length > 0) {
      if (enableCommoditiesDatabase) {
        const tripCommodities = response.trip_commodities.map((trComm: TripCommodities) => {
          const orderData = response.orders?.find((order) => order.id === trComm.order_id) || null;
          return mapSavedTripCommodityToCommodityList(trComm, orderData);
        });
        tripSlice.new_trip_commodities = tripCommodities;
      } else {
        const sliceTripCommodities = response.trip_commodities.map((trComm: TripCommodities) => {
          let orderData = null;
          if (trComm.order_id) {
            // order id is present so we need to get the remaining order data we need for commodities
            orderData = response.orders?.find((order) => order.id === trComm.order_id) || null;
          }

          return {
            ...trComm,
            ui_uid: trComm.id || nanoid(),
            customer: orderData ? orderData.raw_customer?.name : undefined,
            customer_number: orderData ? orderData.customer_order_number : undefined,
            order_readable_id: orderData ? orderData.readable_id : undefined
          };
        });

        tripSlice.trip_commodities = sliceTripCommodities;
      }
    }

    dispatch(setTripSliceState(tripSlice));
  };

  const getTripData = useCallback(() => {
    if (!tripId) return;

    setLoading(true);
    getTripById(tripId)
      .unwrap()
      .then((response) => {
        setOriginalTripData(response);
        tripResponseToSlice(response);
      })
      .catch((error) => {
        addNotification({
          variant: 'error',
          message: error.message || 'Error getting trip data, please try again'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tripId]);

  const handleIgnoreAndProceed = (): void => {
    setOpenWarningHelper(false);
    handleSubmitEdit(true);
  };

  const handleCloseWarningHelper = (): void => {
    setIsSubmitting(false);
    setOpenWarningHelper(false);
  };

  // #endregion

  // #region Effects

  useEffect(() => {
    if (openEdit) {
      getTripData();
    }
  }, [openEdit, getTripData]);

  // #endregion

  return (
    <>
      <TwDialog
        open={openEdit}
        onClickClose={(): void => setIsOpenWarning(true)}
        fullScreen
        title={`Edit trip #${tripData.readable_id ?? ''}`}
        actions={[
          <TwButton
            key="1"
            type={TwButtonType.secondary}
            label="Cancel"
            action={(): void => {
              setIsOpenWarning(true);
            }}
          />,
          <TwButton
            key="2"
            type={TwButtonType.primary}
            label="Save"
            disabled={isSubmitting}
            action={handleFormSubmit}
          />
        ]}
      >
        {loading && (
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: 10
            }}
            open={true}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        {!loading && (
          <Grid container spacing={6}>
            {matchesAboveLarge && (
              <QuickMenuItems
                isOrderInfoVisible={isOrderInfoVisible}
                isRouteInfoVisible={isRouteInfoVisible}
                isDispatchInfoVisible={isDispatchInfoVisible}
                isAttachmentsVisible={isAttachmentsVisible}
                tripsVisibilities={[false]}
                orderInfoRef={orderInfoRef}
                routeInfoRef={routeInfoRef}
                dispatchRef={dispatchRef}
                attachmentsRef={attachmentsInfoRef}
                tripsRef={null}
                entity={EntityType.TRIP}
              />
            )}
            <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
              <TripWrapperForm onSubmit={handleSubmitEdit} formRef={formRef}>
                <DataContainer ref={containerRef}>
                  <TripOrderInfo
                    ref={orderInfoRef}
                    dataContainerSx={{
                      width: '100% !important',
                      maxWidth: `${858 / 16}rem`,
                      marginBottom: '3rem'
                    }}
                  />
                  <TripRouteInfo
                    ref={routeInfoRef}
                    routeContainerSx={{
                      width: '100% !important',
                      maxWidth: `${858 / 16}rem`,
                      marginTop: '3rem'
                    }}
                    className="create-trip__route"
                  />
                  <DispatchInfo
                    ref={dispatchRef}
                    dispatchContainerSx={{
                      width: '100% !important',
                      marginBottom: '3rem',
                      marginTop: '3rem',
                      maxWidth: `${858 / 16}rem`
                    }}
                    entity={EntityType.TRIP}
                  />
                  <AttachmentsInfo
                    ref={attachmentsInfoRef}
                    additionalContainerSx={{
                      width: '100% !important',
                      maxWidth: `${858 / 16}rem`,
                      marginBottom: '3rem'
                    }}
                    entity={EntityType.TRIP}
                  />
                </DataContainer>
              </TripWrapperForm>
            </Grid>
            {matchesAboveLarge && <Grid item lg={2} xl={2}></Grid>}
          </Grid>
        )}
      </TwDialog>
      <TwLeaveWarning
        isOpenWarning={isOpenWarning}
        closeWarning={handleWarningClose}
        title={'Leave page?'}
        warningMessage={
          <>
            You haven’t finished creating a trip.
            <br /> Do you want to leave without finishing?
          </>
        }
        handleClose={handleCloseEditTrip}
        closeLabel={'Close without saving'}
        saveAndCloseLabel={'Continue editing'}
        saveAndContinue={handleWarningClose}
      />
      <TwNotifyDriver
        open={openNotify}
        driversToNotify={driversToNotify}
        unassignedDriversToNotify={unassignedDriversToNotify}
        onClose={closeNotify}
        isSplitLoad={false}
        isSegmentShare={true}
        isForNewLoad={false}
        isNormalShare={false}
        tripId={tripId}
      />
      {driver_pay_confirmation_modal.open && (
        <TwDriverPayConfirmationModal open={driver_pay_confirmation_modal.open} />
      )}
      {enableWarningHelper && (
        <TwWarningHelper
          validations={validations}
          open={openWarningHelper}
          handleClose={handleCloseWarningHelper}
          onContinue={handleIgnoreAndProceed}
        />
      )}
    </>
  );
};

export default EditTrip;
